import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  Image,
  Badge,
  Button,
  Modal
} from 'react-bootstrap';
import { 
  FaBuilding,
  FaHammer,
  FaHome,
  FaHandHoldingUsd,
  FaPaintRoller,
  FaGraduationCap,
  FaChartBar,
  FaPalette,
  FaBookOpen,
  FaArrowRight,
  FaRocket,
  FaStar,
  FaUsers,
  FaClock,
  FaCheck,
  FaSearch,
  FaShieldAlt,
  FaLightbulb,
  FaHeadset,
  FaSync,
  FaLayerGroup,
  FaCloud,
  FaServer,
  FaGlobe,
  FaCode,
  FaDatabase,
  FaMobileAlt,
  FaCogs,
  FaBullhorn,
  FaPenNib,
  FaShoppingCart
} from 'react-icons/fa';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

// Move serviceIcons outside the component to prevent recreation on every render
const SERVICE_ICONS = {
  'architecture': <FaBuilding />,
  'architect': <FaBuilding />,
  'construction': <FaHammer />,
  'build': <FaHammer />,
  'renovation': <FaHome />,
  'remodel': <FaHome />,
  'investment': <FaHandHoldingUsd />,
  'finance': <FaChartBar />,
  'interior': <FaPaintRoller />,
  'design': <FaPalette />,
  'education': <FaGraduationCap />,
  'training': <FaBookOpen />,
  'hosting': <FaServer />,
  'cloud': <FaCloud />,
  'web': <FaGlobe />,
  'development': <FaCode />,
  'database': <FaDatabase />,
  'mobile': <FaMobileAlt />,
  'cogs': <FaCogs />,
  'marketing': <FaBullhorn />,
  'creative': <FaPenNib />,
  'ecommerce': <FaShoppingCart />,
  'default': <FaLayerGroup />
};

const ServiceDetails = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedService, setSelectedService] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Gradient backgrounds for cards
  const cardGradients = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)',
    'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)'
  ];

  // Helper functions wrapped in useCallback
  const getShortDescription = useCallback((description) => {
    if (!description) return '';
    const firstPara = description.split('\n\n')[0];
    return firstPara.length > 150 ? firstPara.substring(0, 150) + '...' : firstPara;
  }, []);

  const extractFeatures = useCallback((description) => {
    if (!description) return [];
    
    const commonFeatures = [
      'Comprehensive Planning',
      'Expert Consultation',
      'Quality Assurance',
      'Timely Delivery',
      'Cost-Effective Solutions',
      'Sustainable Practices'
    ];
    
    const featureKeywords = [
      'design', 'planning', 'construction', 'management', 
      'consultation', 'development', 'implementation', 'support',
      'maintenance', 'training', 'analysis', 'optimization'
    ];
    
    const extracted = [];
    featureKeywords.forEach(keyword => {
      if (description.toLowerCase().includes(keyword)) {
        extracted.push(keyword.charAt(0).toUpperCase() + keyword.slice(1));
      }
    });
    
    return extracted.slice(0, 4).length > 0 ? extracted.slice(0, 4) : commonFeatures.slice(0, 3);
  }, []);

  const getImageUrl = useCallback((path) => (path ? `${UPLOAD_BASE_URL}${path}` : null), []);

  const getServiceIcon = useCallback((serviceName) => {
    const lowerName = serviceName.toLowerCase();
    for (const [key, icon] of Object.entries(SERVICE_ICONS)) {
      if (lowerName.includes(key)) return icon;
    }
    return SERVICE_ICONS.default;
  }, []);

  // Fetch services function wrapped in useCallback
  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${BASE_URL}/services.php`);
      const result = await response.json();

      if (result.success && Array.isArray(result.data) && result.data.length > 0) {
        const enhancedServices = result.data.map((service, index) => ({
          ...service,
          rating: (4 + Math.random()).toFixed(1),
          projects: Math.floor(Math.random() * 100) + 50,
          deliveryTime: `${Math.floor(Math.random() * 30) + 7} days`,
          features: extractFeatures(service.description),
          shortDescription: getShortDescription(service.description)
        }));
        setServices(enhancedServices);
      } else {
        setError('No services available');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load services');
    } finally {
      setLoading(false);
    }
  }, [extractFeatures, getShortDescription]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleViewDetails = (service) => {
    setSelectedService(service);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedService(null);
    setShowModal(false);
  };

  const filteredServices = services.filter(service => {
    const matchesSearch = service.service_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (service.shortDescription && service.shortDescription.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  if (loading) {
    return (
      <Container className="services-container py-5">
        <div className="text-center">
          <div className="loading-animation">
            <div className="loading-dots">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
          <h4 className="mt-4 text-gradient">Loading Services</h4>
          <p className="text-muted">Discovering our premium solutions...</p>
        </div>
      </Container>
    );
  }

  if (error || services.length === 0) {
    return (
      <Container className="py-5">
        <Alert variant="danger" className="border-0 shadow-sm">
          <Alert.Heading>Services Not Available</Alert.Heading>
          <p>{error}</p>
          <Button variant="primary" onClick={fetchServices}>
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <div className="services-portal">
      {/* Hero Section */}
      <div className="services-hero">
        <Container className="py-5">
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <Badge bg="primary" className="hero-badge mb-3">
                <FaStar className="me-2" /> Premium Services
              </Badge>
              <h1 className="display-5 fw-bold text-white mb-3">
                Our Professional <span className="text-gradient">Services</span>
              </h1>
              <p className="lead text-white-50 mb-4">
                Explore our comprehensive range of professional services designed 
                to meet your unique needs and exceed expectations.
              </p>
              <div className="hero-stats">
                <div className="stat-item">
                  <h3 className="text-white mb-0">{services.length}+</h3>
                  <small className="text-white-50">Services</small>
                </div>
                <div className="stat-item">
                  <h3 className="text-white mb-0">98%</h3>
                  <small className="text-white-50">Satisfaction</small>
                </div>
                <div className="stat-item">
                  <h3 className="text-white mb-0">24/7</h3>
                  <small className="text-white-50">Support</small>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="hero-visual">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <div className="floating-shape shape-3"></div>
                <div className="service-preview-card">
                  <div className="preview-icon">
                    <FaRocket />
                  </div>
                  <h5 className="mb-2">Expert Solutions</h5>
                  <p className="small mb-0">Professional services for optimal results</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-5">
        {/* Search Section */}
        <div className="search-section mb-5">
          <div className="search-wrapper">
            <div className="search-box">
              <div className="search-icon">
                <FaSearch />
              </div>
              <input
                type="text"
                className="search-input"
                placeholder="Search services by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button 
                  variant="link" 
                  className="search-clear"
                  onClick={() => setSearchTerm('')}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <Row className="g-4">
          {filteredServices.map((service, index) => (
            <Col lg={4} md={6} key={service.id}>
              <div className="service-card-wrapper">
                <Card className="service-card border-0 shadow-lg">
                  <div 
                    className="service-card-header"
                    style={{ background: cardGradients[index % cardGradients.length] }}
                  >
                    <div className="service-icon-wrapper">
                      <div className="service-icon">
                        {getServiceIcon(service.service_name)}
                      </div>
                      {service.rating && (
                        <Badge bg="light" text="dark" className="service-rating-badge">
                          <FaStar className="text-warning me-1" />
                          {service.rating}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Card.Body className="p-4">
                    <h4 className="service-title mb-3">{service.service_name}</h4>
                    
                    <p className="service-description text-muted mb-4">
                      {service.shortDescription || getShortDescription(service.description)}
                    </p>
                    
                    <div className="service-features mb-4">
                      {service.features && service.features.slice(0, 3).map((feature, idx) => (
                        <Badge key={idx} bg="light" text="dark" className="feature-badge me-2 mb-2">
                          <FaCheck className="me-1 text-success" />
                          {feature}
                        </Badge>
                      ))}
                    </div>
                    
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="service-stats">
                        {service.projects && (
                          <div className="stat me-3">
                            <FaUsers className="stat-icon" />
                            <span className="stat-value">{service.projects}+</span>
                            <small className="stat-label">Projects</small>
                          </div>
                        )}
                      </div>
                      <Button
                        variant="primary"
                        className="service-action-btn"
                        onClick={() => handleViewDetails(service)}
                      >
                        View Details
                        <FaArrowRight className="ms-2" />
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </Col>
          ))}
        </Row>

        {/* No Results Message */}
        {filteredServices.length === 0 && (
          <div className="no-results text-center py-5">
            <div className="no-results-icon mb-4">
              <FaSearch size={60} className="text-muted" />
            </div>
            <h4 className="mb-3">No Services Found</h4>
            <p className="text-muted mb-4">Try a different search term</p>
            <Button 
              variant="outline-primary" 
              onClick={() => setSearchTerm('')}
            >
              Show All Services
            </Button>
          </div>
        )}

        {/* Why Choose Us Section */}
        <Row className="mt-5 pt-5">
          <Col lg={12}>
            <div className="section-header text-center mb-5">
              <Badge bg="light" text="dark" className="mb-3 px-3 py-2">
                <FaShieldAlt className="me-2" /> Why Choose Us
              </Badge>
              <h2 className="mb-3">Excellence in Every Service</h2>
              <p className="text-muted">We deliver premium quality with unmatched expertise</p>
            </div>
            
            <Row className="g-4">
              <Col lg={3} md={6}>
                <div className="excellence-card">
                  <div className="excellence-icon">
                    <FaShieldAlt />
                  </div>
                  <h5>Quality Guarantee</h5>
                  <p className="text-muted small">100% satisfaction guaranteed with all our services</p>
                </div>
              </Col>
              <Col lg={3} md={6}>
                <div className="excellence-card">
                  <div className="excellence-icon">
                    <FaSync />
                  </div>
                  <h5>Fast Delivery</h5>
                  <p className="text-muted small">Timely project completion with agile methodologies</p>
                </div>
              </Col>
              <Col lg={3} md={6}>
                <div className="excellence-card">
                  <div className="excellence-icon">
                    <FaHeadset />
                  </div>
                  <h5>24/7 Support</h5>
                  <p className="text-muted small">Round-the-clock support for all your needs</p>
                </div>
              </Col>
              <Col lg={3} md={6}>
                <div className="excellence-card">
                  <div className="excellence-icon">
                    <FaLightbulb />
                  </div>
                  <h5>Innovation</h5>
                  <p className="text-muted small">Cutting-edge solutions for modern challenges</p>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      {/* Service Detail Modal */}
      {selectedService && (
        <Modal 
          show={showModal} 
          onHide={handleCloseModal} 
          centered 
          size="lg"
          className="service-detail-modal"
        >
          <Modal.Header closeButton className="border-0">
            <Modal.Title>
              <div className="d-flex align-items-center">
                <div 
                  className="modal-service-icon me-3"
                  style={{ 
                    background: cardGradients[services.findIndex(s => s.id === selectedService.id) % cardGradients.length]
                  }}
                >
                  {getServiceIcon(selectedService.service_name)}
                </div>
                <div>
                  <h3 className="mb-0">{selectedService.service_name}</h3>
                </div>
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Row className="align-items-center mb-4">
              <Col lg={4} className="text-center mb-4 mb-lg-0">
                {selectedService.image_url ? (
                  <Image
                    src={getImageUrl(selectedService.image_url)}
                    alt={selectedService.service_name}
                    rounded
                    fluid
                    className="modal-service-image"
                  />
                ) : (
                  <div className="modal-service-icon-large">
                    {getServiceIcon(selectedService.service_name)}
                  </div>
                )}
              </Col>
              <Col lg={8}>
                <div className="modal-service-stats">
                  {selectedService.rating && (
                    <div className="stat-item">
                      <FaStar className="text-warning me-2" />
                      <div>
                        <h6 className="mb-0">{selectedService.rating}/5</h6>
                        <small>Rating</small>
                      </div>
                    </div>
                  )}
                  {selectedService.projects && (
                    <div className="stat-item">
                      <FaUsers className="text-primary me-2" />
                      <div>
                        <h6 className="mb-0">{selectedService.projects}+</h6>
                        <small>Projects</small>
                      </div>
                    </div>
                  )}
                  {selectedService.deliveryTime && (
                    <div className="stat-item">
                      <FaClock className="text-info me-2" />
                      <div>
                        <h6 className="mb-0">{selectedService.deliveryTime}</h6>
                        <small>Avg. Delivery</small>
                      </div>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
            
            <div className="modal-content">
              <h5 className="mb-3">Service Overview</h5>
              <div className="service-full-description">
                {selectedService.description.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="mb-3">
                    {paragraph}
                  </p>
                ))}
              </div>
              
              {selectedService.features && selectedService.features.length > 0 && (
                <>
                  <h5 className="mt-4 mb-3">Key Features</h5>
                  <div className="feature-list mb-4">
                    {selectedService.features.map((feature, index) => (
                      <div key={index} className="feature-item">
                        <FaCheck className="text-success me-2" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="outline-secondary" onClick={handleCloseModal}>
              Close
            </Button>
            <Button variant="primary">
              <FaRocket className="me-2" />
              Get Started
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <style>{`
        .services-portal {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          min-height: 100vh;
        }
        
        .services-hero {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          position: relative;
          overflow: hidden;
        }
        
        .services-hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.1' fill-rule='evenodd'/%3E%3C/svg%3E");
          opacity: 0.3;
        }
        
        .hero-badge {
          font-size: 0.9rem;
          padding: 8px 16px;
          border-radius: 20px;
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }
        
        .text-gradient {
          background: linear-gradient(90deg, #f093fb, #f5576c);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .hero-stats {
          display: flex;
          gap: 30px;
          margin-top: 30px;
        }
        
        .stat-item {
          display: flex;
          flex-direction: column;
        }
        
        .hero-visual {
          position: relative;
          height: 300px;
        }
        
        .floating-shape {
          position: absolute;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          animation: float 6s ease-in-out infinite;
        }
        
        .shape-1 {
          width: 100px;
          height: 100px;
          top: 20px;
          right: 50px;
          animation-delay: 0s;
        }
        
        .shape-2 {
          width: 70px;
          height: 70px;
          bottom: 40px;
          right: 100px;
          animation-delay: 2s;
        }
        
        .shape-3 {
          width: 50px;
          height: 50px;
          top: 100px;
          right: 150px;
          animation-delay: 4s;
        }
        
        .service-preview-card {
          position: absolute;
          top: 50%;
          left: 0;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.95);
          padding: 20px;
          border-radius: 15px;
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
          width: 200px;
        }
        
        .preview-icon {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #667eea, #764ba2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 24px;
          margin-bottom: 15px;
        }
        
        .search-section {
          max-width: 600px;
          margin: 0 auto;
        }
        
        .search-wrapper {
          position: relative;
        }
        
        .search-box {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .search-icon {
          position: absolute;
          left: 20px;
          color: #6c757d;
          z-index: 2;
        }
        
        .search-input {
          width: 100%;
          padding: 15px 20px 15px 50px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: white;
          padding-right: 100px;
        }
        
        .search-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 5px 15px rgba(102, 126, 234, 0.1);
        }
        
        .search-clear {
          position: absolute;
          right: 10px;
          padding: 5px 10px;
          font-size: 0.875rem;
          text-decoration: none;
        }
        
        .service-card-wrapper {
          position: relative;
          height: 100%;
        }
        
        .service-card {
          border-radius: 16px;
          overflow: hidden;
          transition: all 0.4s ease;
          height: 100%;
        }
        
        .service-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15) !important;
        }
        
        .service-card-header {
          height: 120px;
          position: relative;
          padding: 20px;
        }
        
        .service-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        
        .service-icon {
          width: 60px;
          height: 60px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          color: white;
          backdrop-filter: blur(10px);
        }
        
        .service-rating-badge {
          font-size: 0.875rem;
          padding: 6px 12px;
          border-radius: 20px;
        }
        
        .service-title {
          font-weight: 600;
          color: #333;
        }
        
        .service-description {
          line-height: 1.6;
          min-height: 80px;
        }
        
        .service-features {
          min-height: 60px;
        }
        
        .feature-badge {
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.8rem;
          display: inline-flex;
          align-items: center;
        }
        
        .service-stats {
          display: flex;
          align-items: center;
        }
        
        .stat {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .stat-icon {
          font-size: 18px;
          color: #667eea;
        }
        
        .stat-value {
          font-weight: 600;
          font-size: 0.9rem;
        }
        
        .stat-label {
          font-size: 0.75rem;
          color: #6c757d;
        }
        
        .service-action-btn {
          padding: 8px 20px;
          border-radius: 8px;
          font-weight: 500;
          display: flex;
          align-items: center;
        }
        
        .loading-animation {
          display: flex;
          justify-content: center;
        }
        
        .loading-dots {
          display: flex;
          gap: 10px;
        }
        
        .dot {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: linear-gradient(135deg, #667eea, #764ba2);
          animation: bounce 1.4s infinite ease-in-out;
        }
        
        .dot:nth-child(1) { animation-delay: -0.32s; }
        .dot:nth-child(2) { animation-delay: -0.16s; }
        
        .excellence-card {
          background: white;
          padding: 30px;
          border-radius: 15px;
          text-align: center;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
          height: 100%;
        }
        
        .excellence-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
        }
        
        .excellence-icon {
          width: 70px;
          height: 70px;
          margin: 0 auto 20px;
          background: linear-gradient(135deg, #667eea, #764ba2);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 28px;
        }
        
        .modal-service-icon {
          width: 60px;
          height: 60px;
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 28px;
        }
        
        .modal-service-icon-large {
          width: 120px;
          height: 120px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
          font-size: 48px;
          margin: 0 auto;
        }
        
        .modal-service-image {
          max-width: 100%;
          height: auto;
          border-radius: 15px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }
        
        .modal-service-stats {
          display: flex;
          gap: 30px;
          justify-content: center;
        }
        
        .modal-service-stats .stat-item {
          display: flex;
          align-items: center;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 12px;
          min-width: 120px;
        }
        
        .service-full-description {
          line-height: 1.8;
          color: #333;
        }
        
        .feature-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 12px;
        }
        
        .feature-item {
          display: flex;
          align-items: center;
          padding: 10px 15px;
          background: #f8f9fa;
          border-radius: 8px;
          border-left: 4px solid #28a745;
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
        
        @media (max-width: 768px) {
          .hero-stats {
            flex-direction: column;
            gap: 15px;
          }
          
          .modal-service-stats {
            flex-direction: column;
            gap: 15px;
            align-items: center;
          }
          
          .modal-service-stats .stat-item {
            width: 100%;
            max-width: 200px;
          }
          
          .feature-list {
            grid-template-columns: 1fr;
          }
        }
        
        @media (max-width: 576px) {
          .hero-visual {
            height: 200px;
          }
          
          .service-preview-card {
            width: 150px;
            padding: 15px;
          }
          
          .modal-service-icon {
            width: 50px;
            height: 50px;
            font-size: 24px;
          }
          
          .modal-service-icon-large {
            width: 100px;
            height: 100px;
            font-size: 40px;
          }
        }
      `}</style>
    </div>
  );
};

export default ServiceDetails;